import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Rocket, Building2, ExternalLink, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { startups } from '../data/mock';

const StartupsPage = () => {
  return (
    <Layout>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .startup-hero {
          background: linear-gradient(135deg, #000a14 0%, #001a33 30%, #002952 70%, #003d7a 100%);
        }
        .cyber-grid {
          background-image: 
            linear-gradient(rgba(0, 102, 204, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 102, 204, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        .startup-card {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .startup-card:hover {
          transform: translateY(-12px);
        }
        .startup-card:hover .card-glow {
          opacity: 1;
        }
        .startup-card:hover .startup-logo {
          transform: scale(1.05);
        }
        .card-glow {
          position: absolute;
          inset: -2px;
          border-radius: 1.5rem;
          opacity: 0;
          transition: opacity 0.5s ease;
          z-index: -1;
        }
        .gradient-border {
          background: linear-gradient(135deg, #0066cc, #00d4ff, #ff9500, #0066cc);
          background-size: 300% 300%;
          animation: gradient-shift 5s ease infinite;
        }
        .text-gradient-blue {
          background: linear-gradient(135deg, #00a3e0 0%, #00d4ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Hero Section */}
      <section className="startup-hero relative py-28 md:py-36 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 cyber-grid opacity-40" />
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-3xl" style={{animation: 'pulse-glow 4s ease-in-out infinite'}} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-blue-600/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-cyan-500/20 text-cyan-400 px-5 py-2.5 rounded-full mb-8 border border-cyan-500/30 backdrop-blur-sm">
              <Rocket className="w-5 h-5" />
              <span className="font-semibold">Innovation & Entrepreneurship</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              BIT Startups &<br />
              <span className="text-gradient-blue">
                Innovation Hub
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed max-w-3xl mx-auto">
              Discover emerging technology companies and innovative ventures born from the Baobab Institute of Tanzania's entrepreneurship ecosystem.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25">
                Explore Startups
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg font-semibold backdrop-blur-sm">
                  Partner With Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-[#001a33] to-[#002952]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="group">
              <p className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2 group-hover:scale-110 transition-transform">{startups.length}</p>
              <p className="text-white/70 font-medium">Active Startups</p>
            </div>
            <div className="group">
              <p className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2 group-hover:scale-110 transition-transform">20+</p>
              <p className="text-white/70 font-medium">Services Offered</p>
            </div>
            <div className="group">
              <p className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2 group-hover:scale-110 transition-transform">2024</p>
              <p className="text-white/70 font-medium">Year Founded</p>
            </div>
            <div className="group">
              <p className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2 group-hover:scale-110 transition-transform">∞</p>
              <p className="text-white/70 font-medium">Possibilities</p>
            </div>
          </div>
        </div>
      </section>

      {/* Startups Grid */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#0066cc]/10 text-[#0066cc] border-[#0066cc]/20">
              <Building2 className="w-4 h-4 mr-2" />
              Our Ventures
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Featured Startups
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Innovative companies driving digital transformation and creating solutions for tomorrow's challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {startups.map((startup) => (
              <Link key={startup.id} to={`/startups/${startup.id}`} className="block group">
                <div className="startup-card relative bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100 h-full">
                  {/* Glow Effect */}
                  <div className="card-glow gradient-border" />
                  
                  {/* Card Header with Logo */}
                  <div className="relative p-8 pb-4"
                       style={{ background: startup.colors.gradient }}>
                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-10"
                         style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                    
                    {/* Status Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {startup.status === 'active' ? 'Active' : 'Coming Soon'}
                      </Badge>
                    </div>

                    {/* Logo */}
                    <div className="relative flex justify-center">
                      <div className="startup-logo bg-black/30 backdrop-blur-xl rounded-2xl p-4 transition-transform duration-500">
                        <img 
                          src={startup.logo} 
                          alt={`${startup.name} Logo`}
                          className="w-32 h-32 md:w-40 md:h-40 object-contain"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-8 pt-6">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 group-hover:text-[#0066cc] transition-colors">
                      {startup.name}
                    </h3>
                    <p className="text-base font-semibold mb-4" style={{ color: startup.colors.primary }}>
                      {startup.tagline}
                    </p>
                    <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed text-justify">
                      {startup.description}
                    </p>

                    {/* Services Preview */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {startup.services.slice(0, 4).map((service, idx) => (
                        <span key={idx} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                          {service.name}
                        </span>
                      ))}
                      {startup.services.length > 4 && (
                        <span className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                          +{startup.services.length - 4} more
                        </span>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-500">
                        Est. {startup.founded} • {startup.location}
                      </span>
                      <span className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: startup.colors.primary }}>
                        Explore
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Join Ecosystem CTA */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #001a33 0%, #002952 50%, #003d7a 100%)' }}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-8 bg-cyan-400/20 text-cyan-400 border-cyan-400/30 px-6 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Join Our Ecosystem
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Have an Innovative Idea?
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              BIT's Innovation Hub supports aspiring entrepreneurs and startups. Join our ecosystem and turn your ideas into reality with world-class resources and mentorship.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-cyan-500/25">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/programmes">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg font-semibold backdrop-blur-sm">
                  Explore Programmes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default StartupsPage;
